import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { PageComponent, ICC_PAGE_DATA } from '@icc/helpers';
import { _, SharedFacade } from '@icc/configurator/shared';
import { Casing, Profile, ProfileType, Reinforcement } from '@icc/window';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import {
    TranslateService,
    ConfigurationsService,
    APP_CONFIG,
    AppConfigFactory,
    core,
} from '@icc/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProfileColorsService } from '../profile-colors.service';
import { ProfilesPriceService } from '@icc/common/profiles-price.service';
import { CurrentConfiguratorService } from '@icc/common/configurators/current-configurator.service';
import { PriceBaseService } from '@icc/price/b2b';
import { UnitConverterService } from '@icc/configurator/ui';
import { ReinforcementsModalService } from '../../reinforcements/reinforcements-modal.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { casingValidator } from './casing-validator';
import { MatCheckbox } from '@angular/material/checkbox';
import { CasingsService } from '../casings.service';

@Component({
    selector: 'icc-profile-options-page',
    templateUrl: './profile-options-page.component.html',
    styleUrls: ['./profile-options-page.component.scss'],
})
export class ProfileOptionsPageComponent extends PageComponent implements OnInit, OnDestroy {
    public title: string = _('ACCESSORY|Opcje profilu');

    profile: Profile | null = null;
    form: FormGroup;

    private casingFormSubscriptions = new Subscription();
    casingForm!: FormGroup;
    casingWallOverlap: number[] = [];
    casings: any = [];
    casing: Casing | null = null;
    isCasingOptionsSelected = false;
    isCasingAvailableForFrame = false;
    selectedCasing: any;
    CASING_GAP = 10;
    lowestInnerCasingWidth = 0;
    highestInnerCasingWidth = 0;
    innerCasingWidth = 0;
    casingWallWidth!: number;
    casingHitchPoint = 0;

    focused = false;
    showColorsWhenCoupling = this.currentConfiguratorService.conf === 'coupled_window';
    showColorsInComplementary = this.currentConfiguratorService.conf === 'complementary_goods';
    showCoreColorInAdditionalProfilesAndAccessories = this.config().IccConfig.Configurators
        .showCoreColorInAdditionalProfilesAndAccessories;
    currency: any = null;
    optionsInComplementary = this.currentConfiguratorService.conf === 'complementary_goods';

    dimensionUnit = this.unitConverterService.getUnit();

    constructor(
        private translateService: TranslateService,
        private configurationsService: ConfigurationsService<'window'>,
        private currentConfiguratorService: CurrentConfiguratorService,
        private priceBaseService: PriceBaseService,
        private sharedFacade: SharedFacade,
        private profileColorsService: ProfileColorsService,
        private profilesPriceService: ProfilesPriceService,
        private fb: FormBuilder,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        @Inject(ICC_PAGE_DATA)
        private pageData: {
            profile: Profile;
            profilesPrices: any[];
            profile$: BehaviorSubject<Profile>,
            selectedColors$: BehaviorSubject<any>
        },
        private unitConverterService: UnitConverterService,
        private reinforcementsModalService: ReinforcementsModalService,
        private casingsService: CasingsService
    ) {
        super();
    }

    ngOnInit() {
        if(this.pageData.profile.onlyWithCasingCover && this.currentConfiguratorService.conf !== 'complementary_goods') {
            this.isCasingOptionsSelected = true;
            this.casingsService.setIsCasingAvailableForFrame(true);
        }

        if(this.currentConfiguratorService.conf !== 'complementary_goods') {
            this.casings = this.casingsService.matchCasingsToFrame(this.pageData.profile?.id);
            if (this.casings.length > 0) {
                this.setInitialCasingSetup(this.casings);
                this.createCasingFormGroup(this.casings, this.casingHitchPoint);

                this.casingFormSubscriptions.add(this.casingForm.get('casingWallWidth')?.valueChanges.pipe(distinctUntilChanged())
                    .subscribe((wallWidth) => {
                        const casing = this.casingsService.getCasingBasedOnWallWidth(
                            Number(wallWidth),
                            this.casingHitchPoint,
                            this.casings
                        );
                        this.innerCasingWidth = wallWidth - this.casingHitchPoint;
                        this.casingWallOverlap = this.casingsService.getWallOverlapsBasedOnInnerCasingWidth(casing, this.innerCasingWidth);
                        this.casingForm.get('casingWallOverlap')?.patchValue(this.casingWallOverlap[0], { emitEvent: false, });
                        this.changeCasingBasedOnInnerWidthAndWallOverlap();
                    })
                );
            }
        }
        this.profile = this.pageData.profile;

        this.form = this.fb.group({
            count: 1,
            currentLength: [0, [Validators.min(0), Validators.required]],
            width: [this.profile.width || 0],
            comment: '',
        });

        this.pageData.selectedColors$.subscribe(c => {
            this.profile.selectedColor = c;
        });

        this.sharedFacade.currency$.subscribe(currency => {
            this.currency = currency;
        });

        this.pageData.profile$.subscribe(p => {
            this.form.patchValue(p);
        });

    }

    changeCasingBasedOnInnerWidthAndWallOverlap() {
        this.selectedCasing = this.casingsService.getCasingBasedOnInnerWidthAndWallOverlap(
            this.casingForm.get('casingWallWidth')?.value - this.casingHitchPoint,
            this.casingForm.get('casingWallOverlap')?.value,
            this.casings
        );
    }

    ngOnDestroy() {
        this.casingFormSubscriptions.unsubscribe();
    }

    setInitialCasingSetup(casings: any, conf = this.configurationsService.conf.Current) {
        this.isCasingAvailableForFrame = this.casingsService.isCasingAvailableForFrame;
        this.casingHitchPoint = this.pageData.profile.casingHitchPoint;
        this.isCasingOptionsSelected = conf.Casing?.isCasingSelected;
        this.lowestInnerCasingWidth = Math.min(...casings.map(c => c.innerCasingWidth)) + this.casingHitchPoint;
        this.highestInnerCasingWidth = Math.max(...casings.map(c => c.innerCasingWidth)) + this.casingHitchPoint;
    }

    createCasingFormGroup(casings: any, casingHitchPoint: number, conf = this.configurationsService.conf.Current) {
        let casingWallWidth;
        let casingWallOverlap;
        const isSelectedCasingAvailable = casings.find((c) => c.id === conf.Casing.id);

        if (isSelectedCasingAvailable) {
            this.casingWallOverlap = this.casingsService.getWallOverlapsBasedOnInnerCasingWidth(this.casings, isSelectedCasingAvailable.innerCasingWidth);
            casingWallWidth = Number(conf.Casing?.innerWidth) + casingHitchPoint;
            casingWallOverlap = conf.Casing?.wallOverlap;
        }

        this.casingForm = this.fb.group({
            casingWallWidth: [casingWallWidth ? casingWallWidth : this.casingWallWidth, {
                validators: [
                    Validators.min(Number(this.lowestInnerCasingWidth)),
                    Validators.max(Number(this.highestInnerCasingWidth)),
                    casingValidator(this.lowestInnerCasingWidth, this.highestInnerCasingWidth, this.CASING_GAP)],
                updateOn: 'change'
            }],
            casingWallOverlap: [casingWallOverlap ? casingWallOverlap : casings[0].casingWallOverlap],
        });
    }

    toggleCasing(event: MatCheckbox) {
        if (event.checked) {
            this.isCasingOptionsSelected = true;
            this.casingsService.setIsCasingAvailableForFrame(true);
        } else {
            this.isCasingOptionsSelected = false;
            this.casingsService.setIsCasingAvailableForFrame(false);
        }
    }

    add(conf = this.configurationsService.conf.Current) {
        if (this.currentConfiguratorService.conf !== 'complementary_goods'
            && this.currentConfiguratorService.conf !== 'coupled_window'
        ) {
            const selectedCasing = this.selectedCasing || conf.Casing;
            this.isCasingAvailableForFrame = this.casingsService.isCasingAvailableForFrame;

            if (this.isCasingAvailableForFrame !== true) {
                this.casingsService.unsetCasing();
            } else {
                this.casingsService.setCasing(
                    selectedCasing,
                    this.casingForm.get('casingWallWidth')?.value - this.casingHitchPoint,
                    this.casingForm.get('casingWallOverlap')?.value,
                    this.casingHitchPoint
                );
            }
        }
        Object.assign(this.profile, this.form.value);
        this.sharedFacade.closePage(this.profile);
    }

    getPrice() {
        let price = null;
        if (this.profile && this.profile.selectedColor) {
            price = this.profilesPriceService.getProfilePrice(
                this.profile.id,
                this.profile.type,
                null,
                this.profile.selectedColor,
                this.pageData.profilesPrices
            );
        }
        return !isNaN(price) && price !== null
            ? price
            : this.translateService.instant('CONFIGURATOR|Do wyceny');
    }

    openModalColors() {
        if (this.profile) {
            this.profileColorsService
                .openProfileColorsModal(
                    core.copy(this.profile),
                    core.copy(this.profile),
                    this.pageData.profilesPrices,
                    this.pageData.selectedColors$,
                    true
                )
                .then(selection => {
                    if (this.profile) {
                        this.profile.price = this.profilesPriceService.getProfilePrice(
                            this.profile.id,
                            this.profile.type,
                            null,
                            selection ? selection.colors : this.profile.selectedColor,
                            this.pageData.profilesPrices
                        );

                        this.profile.selectedColor = core.copy( selection ? selection.colors : this.profile.selectedColor);
                        this.profile.selectedWood = core.copy(
                            selection ? selection.wood : this.profile.selectedWood
                        );
                        this.pageData.profile$.next(this.form.value);
                    }
                });
        }
    }

    openModalReinforcements(profile: Profile) {
        this.reinforcementsModalService.openReinforcementsModal(
            profile.id,
            profile.reinforcement ? profile.reinforcement : null
        ).then((reinforcement: Reinforcement | false) => {
            if (reinforcement) {
                if (this.profile) {
                    this.profile.reinforcement = reinforcement.id === null ? null : core.copy(reinforcement);
                }
            }
        });

    }

    hasZeroPrice(
        price: {
            price_field: any;
            price_length: any;
            price_construction: any;
            price_piece: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return false;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece, price.price_percent];
        if (type === 'threshold') {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (type && ['coupling', 'extension', 'alignment', 'other'].includes(type)) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (type === 'sandwich') {
            requiredFields = [price.price_area];
        } else if (type === 'frame') {
            requiredFields.push(price.price_construction);
        }
        return requiredFields.every(p => p === 0);
    }

    hasNoPrice(
        price: {
            price_field: any;
            price_length: any;
            price_piece: any;
            price_construction: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return true;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece, price.price_percent];
        if (type === 'threshold') {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (type && ['coupling', 'extension', 'alignment', 'other'].includes(type)) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (type === 'sandwich') {
            requiredFields = [price.price_area];
        } else if (type === 'frame') {
            requiredFields.push(price.price_construction);
        }
        return requiredFields.some(p => isNaN(Number(p)) || p === null);
    }

    priceMarket(price: number) {
        return this.priceBaseService.addMarginMarketFactor(price);
    }
}
